import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Sparkles, Power, DollarSign, Loader2, AlertTriangle, Save, Cpu, Gauge } from "lucide-react";
import { TruckAiAccuracyCard } from "@/components/TruckAiAccuracyCard";
import { PhotoUsageCard } from "@/components/PhotoUsageCard";
import { AiAccuracyCard } from "@/components/AiAccuracyCard";

const INTENT_LABELS = {
  describe: "Identify / describe (photo → OCR)",
  pricing: "Pricing research (market low/avg/high)",
  oem_number: "OEM number suggestions",
  interchange: "Interchange / supersession",
};
const RISKY = { oem_number: true, interchange: true };

// Admin-only AI Assist control: master kill switch, per-intent toggles, hard budget cap
// + live spend meter + the planning formula. Dollars live here and nowhere staff can see.
export default function AiSettingsPage() {
  const [b, setB] = useState(null);
  const [cost, setCost] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmMasterOn, setConfirmMasterOn] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/ai/budget"); setB(data);
      api.get("/ai/cost-detail").then(({ data }) => setCost(data)).catch(() => {});
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (payload, msg) => {
    setBusy(true);
    try { await api.put("/ai/config", payload); toast.success(msg || "Saved"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!b) return <div className="font-mono text-xs text-slate-400 py-10">LOADING…</div>;
  const cfg = b.config;
  const pct = b.pct || 0;
  const meterColor = pct >= 100 ? "bg-red-600" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div data-testid="ai-settings-page" className="max-w-2xl">
      <div className="flex items-center gap-2 mb-1"><Sparkles className="w-6 h-6 text-violet-600" /><h1 className="font-display text-2xl font-bold tracking-tight">AI Assist</h1></div>
      <p className="text-sm text-slate-500 mb-6">On-demand, human-verified. Read-numbers (OCR) &amp; describe are live; pricing &amp; interchange arrive in a later slice. Dollar figures are admin-only — staff never see them.</p>

      {/* MASTER kill switch */}
      <div data-testid="ai-master-card" className="rounded-xl border border-slate-200 bg-white p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Power className={`w-5 h-5 ${cfg.master_enabled ? "text-emerald-600" : "text-slate-400"}`} />
            <div><div className="font-bold">Master AI switch</div><div className="text-xs text-slate-500">Kill switch — turns ALL AI assist off instantly, no redeploy.</div></div>
          </div>
          <Switch data-testid="ai-master-toggle" checked={cfg.master_enabled} disabled={busy}
            onCheckedChange={(v) => { if (v) setConfirmMasterOn(true); else patch({ master_enabled: false }, "AI assist OFF (kill switch)"); }} />
        </div>
      </div>

      <AlertDialog open={confirmMasterOn} onOpenChange={setConfirmMasterOn}>
        <AlertDialogContent data-testid="ai-master-confirm" className="rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Turn AI Assist ON?</AlertDialogTitle>
            <AlertDialogDescription>
              This enables live, billable AI calls for everyone with the "Use AI Assist" permission. Spend is capped by your monthly budget and every call is logged. You can flip this kill switch back off any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ai-master-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="ai-master-confirm-apply" className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => { setConfirmMasterOn(false); patch({ master_enabled: true }, "AI assist ON"); }}>
              Turn AI on
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Per-intent toggles */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4">
        <div className="font-bold mb-1">Intents</div>
        <div className="text-xs text-slate-500 mb-3">Turn individual capabilities on/off. The riskier number-suggestion intents are off by default.</div>
        {Object.keys(INTENT_LABELS).map((k) => (
          <div key={k} data-testid={`ai-intent-row-${k}`} className="flex items-center justify-between py-2 border-t border-slate-100 first:border-0">
            <div className="flex items-center gap-2 text-sm">
              {INTENT_LABELS[k]}
              {RISKY[k] && <Badge className="rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />hallucination-prone</Badge>}
            </div>
            <Switch data-testid={`ai-intent-toggle-${k}`} checked={!!cfg.intents[k]} disabled={busy || !cfg.master_enabled}
              onCheckedChange={(v) => patch({ intents: { ...cfg.intents, [k]: v } }, `${INTENT_LABELS[k]} ${v ? "on" : "off"}`)} />
          </div>
        ))}
      </div>

      {/* OCR / describe model picker */}
      <div data-testid="ai-model-card" className="rounded-xl border border-slate-200 bg-white p-4 mb-4">
        <div className="flex items-center gap-2 mb-1"><Cpu className="w-5 h-5 text-violet-600" /><div className="font-bold">Vision model (read-numbers &amp; describe)</div></div>
        <div className="text-xs text-slate-500 mb-3">Which model reads your tag photos. Gemini 3 Flash is the value pick; bump to Pro for the hardest, greasiest tags. Switch anytime — no code change.</div>
        <Select value={cfg.ocr_model} disabled={busy} onValueChange={(v) => patch({ ocr_model: v }, "Model updated")}>
          <SelectTrigger data-testid="ai-model-select" className="rounded-lg h-11"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-lg">
            {(b.model_catalog || []).map((m) => (
              <SelectItem key={m.id} value={m.id} data-testid={`ai-model-opt-${m.id}`}>
                {m.label} — ${m.in_per_m}/M in · ${m.out_per_m}/M out
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Pricing / interchange research backend */}
      <div data-testid="ai-pricing-card" className="rounded-xl border border-slate-200 bg-white p-4 mb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2"><Cpu className="w-5 h-5 text-violet-600" /><div className="font-bold">Pricing &amp; interchange research</div></div>
          <Badge className={`rounded-full border-0 text-[10px] ${b.search_ready ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{b.search_ready ? "search key configured" : "search key NOT set (dormant)"}</Badge>
        </div>
        <div className="text-xs text-slate-500 mb-3">Search-grounded via Perplexity Sonar — real sold-comps + live citation links, never memory. ~20× the cost of an OCR read; every call's full cost hits your cap + tracker. {b.search_ready ? "" : "Add PERPLEXITY_API_KEY (backend) to light this up."}</div>
        <Select value={cfg.pricing_model} disabled={busy} onValueChange={(v) => patch({ pricing_model: v }, "Pricing tier updated")}>
          <SelectTrigger data-testid="ai-pricing-select" className="rounded-lg h-11"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-lg">
            {(b.pricing_catalog || []).map((m) => (
              <SelectItem key={m.id} value={m.id} disabled={m.deep && !cfg.deep_research_enabled} data-testid={`ai-pricing-opt-${m.id}`}>
                {m.label}{m.deep ? " (deep — enable below first)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
          <div><div className="text-sm font-semibold text-slate-700">Deep Research escalation</div><div className="text-[11px] text-slate-400">Agentic, most thorough on obscure parts — slow + ~10-20× pricier. Back-pocket; off by default.</div></div>
          <Switch data-testid="ai-deep-research-toggle" checked={cfg.deep_research_enabled} disabled={busy}
            onCheckedChange={(v) => patch({ deep_research_enabled: v }, v ? "Deep Research available" : "Deep Research off")} />
        </div>
      </div>

      {/* Budget cap + meter + formula */}
      <div data-testid="ai-budget-card" className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-3"><DollarSign className="w-5 h-5 text-[#B01E28]" /><div className="font-bold">Monthly budget (hard cap)</div></div>
        <div className="mb-3">
          <div className="flex justify-between text-xs mb-1"><span className="text-slate-500">Spent this month ({b.month})</span><span className="font-mono font-bold">${b.spent_usd?.toFixed(2)} / ${b.cap_usd?.toFixed(2)} {b.cap_usd > 0 ? `(${pct}%)` : "(no cap)"}</span></div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${meterColor}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
          <div className="text-[11px] text-slate-400 mt-1">AI stops automatically when the cap is hit (staff see "AI is paused — ask your admin"). Soft alert to admins at 80%.</div>
        </div>
        <BudgetForm cfg={cfg} ceiling={b.projected_ceiling_usd} busy={busy} onSave={patch} cost={cost} />
      </div>

      {/* Live cost tracker — granular per-call est-vs-actual + running averages */}
      <div data-testid="ai-cost-tracker" className="rounded-xl border border-slate-200 bg-white p-4 mt-4">
        <div className="flex items-center gap-2 mb-3"><Gauge className="w-5 h-5 text-violet-600" /><div className="font-bold">Live cost tracker ({cost?.month || b.month})</div></div>
        {!cost || cost.call_count === 0 ? (
          <div className="text-xs text-slate-400">No AI calls yet this month — real unit costs appear here as staff use it.</div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[
                ["Calls", cost.call_count],
                ["Parts", cost.part_count],
                ["Avg / call", `$${cost.avg_cost_per_call_usd?.toFixed(4)}`],
                ["Avg / part", `$${cost.avg_cost_per_part_usd?.toFixed(4)}`],
              ].map(([k, v]) => (
                <div key={k} className="rounded-lg bg-slate-50 px-2 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div>
                  <div className="font-mono font-bold text-sm text-slate-700">{v}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mb-2">Total actual: <b className="font-mono">${cost.total_actual_usd?.toFixed(4)}</b> · est <span className="font-mono">${cost.total_est_usd?.toFixed(4)}</span></div>
            {(cost.by_task || []).length > 0 && (
              <div data-testid="ai-cost-by-task" className="mb-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">By capability ($/call — spot the expensive one)</div>
                <div className="space-y-1">
                  {cost.by_task.map((t) => (
                    <div key={t.task} data-testid={`ai-cost-task-${t.task}`} className="flex items-center gap-2 text-[12px]">
                      <span className="w-40 truncate text-slate-600">{t.label}</span>
                      <span className="font-mono font-bold text-slate-800 w-20">${t.avg_cost_per_call_usd?.toFixed(4)}</span>
                      <span className="font-mono text-slate-400 w-10 text-right">{t.count}×</span>
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-violet-500" style={{ width: `${t.pct_of_spend}%` }} /></div>
                      <span className="font-mono text-slate-400 w-24 text-right">${t.total_usd?.toFixed(4)} · {t.pct_of_spend}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="max-h-56 overflow-y-auto border-t border-slate-100">
              {(cost.calls || []).map((c, i) => (
                <div key={i} data-testid={`ai-cost-row-${i}`} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-50 text-[11px]">
                  <span className="font-mono text-slate-400">{(c.at || "").replace("T", " ").slice(5, 16)}</span>
                  <span className="text-slate-600 truncate flex-1">{c.task}</span>
                  <span className="font-mono text-slate-400">{c.input_tokens}+{c.output_tokens}</span>
                  <span className="font-mono font-bold text-slate-700">${c.actual_cost?.toFixed(5)}</span>
                  <span className={`font-mono ${c.delta > 0 ? "text-red-500" : "text-emerald-600"}`}>{c.delta > 0 ? "+" : ""}{c.delta?.toFixed(5)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <TruckAiAccuracyCard />
      <AiAccuracyCard />
      <PhotoUsageCard />
    </div>
  );
}

function BudgetForm({ cfg, ceiling, busy, onSave, cost: measured }) {
  const [cap, setCap] = useState(cfg.monthly_budget_usd);
  const [cost, setCost] = useState(cfg.cost_per_call_usd);
  const [ppm, setPpm] = useState(cfg.planning.parts_per_month);
  const [cpp, setCpp] = useState(cfg.planning.calls_per_part);
  const liveCeiling = (Number(ppm) || 0) * (Number(cpp) || 0) * (Number(cost) || 0);
  // THE MEASURED TRUTH, beside the guess. The planning figure used to default to $0.15 a call,
  // which overstated every forecast by roughly 4x — his own ledger averages ~$0.037 for a tag
  // read. This reads the actual spend already on this screen; it is never auto-applied.
  const avg = measured?.avg_cost_per_call_usd;
  const reads = (measured?.by_task || []).find((t) => t.task === "read_numbers");
  return (
    <div className="border-t border-slate-100 pt-3">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Plan to your worst-case ceiling</div>
      <div className="grid grid-cols-3 gap-2">
        <div><label className="text-[11px] text-slate-500">Parts / month</label><Input data-testid="ai-plan-ppm" type="number" value={ppm} onChange={(e) => setPpm(e.target.value)} className="rounded-lg h-10 mt-1" /></div>
        <div><label className="text-[11px] text-slate-500">Calls / part</label><Input data-testid="ai-plan-cpp" type="number" value={cpp} onChange={(e) => setCpp(e.target.value)} className="rounded-lg h-10 mt-1" /></div>
        <div><label className="text-[11px] text-slate-500">Held back per call ($)</label><Input data-testid="ai-plan-cost" type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} className="rounded-lg h-10 mt-1" /></div>
      </div>
      {avg != null && (
        <div data-testid="ai-measured-cost-hint" className="text-[11px] text-slate-600 mt-1.5 leading-relaxed">
          This is a <b>worst case</b>, held back before a call so the cap can never be overshot — it is not what a read costs.
          <br />What your reads actually bill, this month, from your own ledger: <b className="font-mono">${avg.toFixed(4)}</b> a call
          {reads ? <> · <b className="font-mono">${reads.avg_cost_per_call_usd.toFixed(4)}</b> to read numbers off a tag ({reads.count} of them)</> : null}.
          {Number(cost) > 0 && avg >= 0.005 && <> You are planning at <b className="font-mono">{(Number(cost) / avg).toFixed(1)}×</b> that.</>}
          {Number(cost) > 0 && avg >= 0.005 && (
            <button type="button" data-testid="ai-use-measured-cost" onClick={() => setCost(Number(avg.toFixed(4)))}
              className="ml-1 underline font-semibold text-[#B01E28]">plan at the real cost instead</button>
          )}
        </div>
      )}
      <div className="text-xs text-slate-600 mt-2 font-mono bg-slate-50 rounded-lg px-3 py-2">
        Max monthly = parts/month × calls/part × cost/call = <b>{Number(ppm) || 0} × {Number(cpp) || 0} × ${Number(cost) || 0}</b> = <b className="text-[#B01E28]">${liveCeiling.toFixed(2)}</b>
      </div>
      <div className="flex items-end gap-2 mt-3">
        <div className="flex-1"><label className="text-[11px] text-slate-500">Hard cap ($ / month) — 0 = no cap</label><Input data-testid="ai-cap-input" type="number" step="1" value={cap} onChange={(e) => setCap(e.target.value)} className="rounded-lg h-10 mt-1" /></div>
        <Button data-testid="ai-budget-save" disabled={busy} className="rounded-lg h-10 bg-[#E0781A] hover:bg-[#C76714] text-white"
          onClick={() => onSave({ monthly_budget_usd: Number(cap), cost_per_call_usd: Number(cost), planning: { parts_per_month: Number(ppm), calls_per_part: Number(cpp) } }, "Budget saved")}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}
        </Button>
      </div>
    </div>
  );
}
